import {
    CreateAgencyActDialog,
    CreateAgencyContractDialog,
} from '@/features/settings/marking/documents'
import { ConfirmDeleteDialog } from '@/features/settings/marking/documents/ConfirmDeleteDialog'
import {
    useDeleteAgencyAct,
    useDeleteAgencyContract,
    useGetAgencyContracts,
    useGetContragents,
    useUpdateAgencyAct,
    useUpdateAgencyContract,
} from '@/shared/api'
import { Table } from '@/shared/components'
import { Button, GreyRubleIconInline } from '@/shared/ui'
import {
    VkOrdAgencyActStatus,
    VkOrdAgencyContractStatus,
} from '@synapex/prisma/client'
import { VkOrdAgencyContractResponse } from '@synapex/shared2/gateway-rest-api'
import { PlusIcon } from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { AgencyContractTableRow } from '../types'
import { FrameworkContractRow } from './FrameworkContractRow'
import { useActiveWorkspace } from '@/true-fsd/data/advertiser-account/hooks'

export const DocumentsWidget = () => {
    const {
        activeAwd,
        ordData: { ordAdditionalData: ordData },
    } = useActiveWorkspace()

    const [openContractDialog, setOpenContractDialog] = useState(false)
    const [openActDialog, setOpenActDialog] = useState(false)
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
    const [deleteType, setDeleteType] = useState<'contract' | 'act' | null>(
        null
    )
    const [deleteId, setDeleteId] = useState<number | null>(null)
    const [editingContract, setEditingContract] =
        useState<VkOrdAgencyContractResponse | null>(null)

    const [currentAgencyContract, setCurrentAgencyContract] =
        useState<null | VkOrdAgencyContractResponse>(null)

    const [editingAct, setEditingAct] = useState<
        VkOrdAgencyContractResponse['agency_acts'][number] | null
    >(null)

    const { data: contragents } = useGetContragents({
        ...activeAwd,
        roles: ['advertiser', 'agency'],
    })

    const { data: agencyContracts } = useGetAgencyContracts(activeAwd)

    const deleteAgencyContract = useDeleteAgencyContract(activeAwd)
    const deleteAgencyAct = useDeleteAgencyAct(activeAwd)

    const advertiser = contragents?.find(
        (contragent) =>
            contragent.ord_contragent_id === ordData?.adOrdContragentId
    )

    const agency = contragents?.find(
        (contragent) => contragent.ord_contragent_id === ordData?.adOrdAgencyId
    )

    const updateAgencyContract = useUpdateAgencyContract(activeAwd)
    const updateAgencyAct = useUpdateAgencyAct(activeAwd)

    const formattedAgencyContracts: AgencyContractTableRow[] = useMemo(() => {
        return (
            agencyContracts?.map((contract) => {
                const acts = contract.agency_acts.map((act) => ({
                    actId: act.vk_ord_agency_act_id,
                    actNumber: act.number,
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                    actStartDate: act.start_date
                        ? new Date(act.start_date)
                        : undefined,
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                    actEndDate: act.end_date
                        ? new Date(act.end_date)
                        : undefined,
                    actDate: new Date(act.date),
                    actBudget: act.budget,
                    actBudgetInApplication: act.budget_in_application,
                    actRemuneration: act.remuneration,
                    actStatus: act.status,
                }))

                return {
                    contractId: contract.vk_ord_agency_contract_id,
                    // createdAt: new Date(contract.created_at),
                    contractNumber: contract.number,
                    contractStartDate: new Date(contract.start_date),
                    status: contract.status,
                    isFrameworkContract: contract.is_framework_contract,
                    hasLinks: contract.hasLinks,
                    type: contract.type,
                    acts,

                    actId: acts[0]?.actId,
                    actNumber: acts[0]?.actNumber,
                    actStartDate: acts[0]?.actStartDate,
                    actEndDate: acts[0]?.actEndDate,
                    actDate: acts[0]?.actDate,
                    actBudget: acts[0]?.actBudget,
                    actRemuneration: acts[0]?.actRemuneration,
                    actBudgetInApplication: acts[0]?.actBudgetInApplication,
                    actStatus: acts[0]?.actStatus,
                } as AgencyContractTableRow
            }) || []
        )
    }, [agencyContracts])

    function handleAddContract() {
        setEditingContract(null)
        setOpenContractDialog(true)
    }

    function handleEditContract(contractId: number) {
        const contract = agencyContracts?.find(
            (contract) => contract.vk_ord_agency_contract_id === contractId
        )
        if (!contract) return
        setEditingContract(contract)
        setOpenContractDialog(true)
    }

    function handleAddAct(contractId: number) {
        const contract = agencyContracts?.find(
            (contract) => contract.vk_ord_agency_contract_id === contractId
        )

        if (!contract) return

        setCurrentAgencyContract(contract)
        setEditingAct(null)
        setOpenActDialog(true)
    }

    function handleEditAct(actId: number | undefined) {
        if (!actId) return
        const contract = agencyContracts?.find((contract) =>
            contract.agency_acts.some(
                (act) => act.vk_ord_agency_act_id === actId
            )
        )
        if (!contract) return
        setCurrentAgencyContract(contract)
        setEditingAct(
            contract.agency_acts.find(
                (act) => act.vk_ord_agency_act_id === actId
            ) ?? null
        )
        setOpenActDialog(true)
    }

    function handleDeleteAct(actId: number) {
        setDeleteType('act')
        setDeleteId(actId)
        setOpenDeleteDialog(true)
    }

    async function confirmDeleteAct(actId: number) {
        try {
            await deleteAgencyAct.mutateAsync({
                actId,
            })
            toast.success('Акт успешно удален')
        } catch (error) {
            toast.error('Произошла ошибка при удалении акта')
            console.error(error)
        }
    }

    function handleUpdateAgencyContractStatus(
        value: VkOrdAgencyContractStatus,
        contractId: number
    ) {
        // check that other agency contracts don't have in_work status
        if (
            value === 'in_work' &&
            agencyContracts?.find(
                (agencyContract) => agencyContract.status === 'in_work'
            )
        ) {
            toast.error(
                'Уже существует активый агентский договор. Сначала закройте его'
            )
            return
        }

        const currentAgencyContract = agencyContracts?.find(
            (agencyContract) =>
                agencyContract.vk_ord_agency_contract_id === contractId
        )
        if (!currentAgencyContract) return

        if (value === 'done' && !currentAgencyContract.used) {
            toast.error('Нельзя закрыть договор без актов')
            return
        }

        if (
            currentAgencyContract.status === 'in_work' &&
            value !== 'in_work' &&
            currentAgencyContract.hasLinks
        ) {
            toast.error(
                'Договор можно завершить только при отправке в ОРД всех актов РА-РР'
            )
            return
        }

        if (currentAgencyContract.agency_acts.length > 0 && value !== 'done') {
            toast.error('Нельзя поняменять статус у договора с активным актом')
            return
        }

        void updateAgencyContract.mutateAsync({
            contractId,
            contract: {
                status: value,
            },
        })
    }

    function handleUpdateAgencyActStatus(
        value: VkOrdAgencyActStatus,
        actId: number | undefined
    ) {
        if (!actId) return
        const agencyContract = agencyContracts?.find((agencyContract) =>
            agencyContract.agency_acts.some(
                (act) => act.vk_ord_agency_act_id === actId
            )
        )
        if (!agencyContract) return

        void updateAgencyAct.mutateAsync({
            actId,
            act: {
                status: value,
                agencyContractId: agencyContract.vk_ord_agency_contract_id,
                creatives: agencyContract.agency_acts
                    .find((act) => act.vk_ord_agency_act_id === actId)
                    ?.vk_ord_creatives.map(
                        (vkOrdcreative) => vkOrdcreative.external_id
                    ),
            },
        })
    }

    function handleDeleteContract(contractId: number) {
        setDeleteType('contract')
        setDeleteId(contractId)
        setOpenDeleteDialog(true)
    }

    async function confirmDeleteContract(contractId: number) {
        try {
            await deleteAgencyContract.mutateAsync({
                contractId,
            })
            toast.success('Договор успешно удален')
        } catch (error) {
            toast.error('Произошла ошибка при удалении договора')
            console.error(error)
        }
    }

    function handleConfirmDelete() {
        if (deleteType === 'contract' && deleteId) {
            void confirmDeleteContract(deleteId)
        } else if (deleteType === 'act' && deleteId) {
            void confirmDeleteAct(deleteId)
        }
        setOpenDeleteDialog(false)
        setDeleteType(null)
        setDeleteId(null)
    }

    function handleCancelDelete() {
        setOpenDeleteDialog(false)
        setDeleteType(null)
        setDeleteId(null)
    }

    const tableHead = (
        <tr>
            <th className="!relative !w-20 !max-w-20 pr-2 !text-start !align-middle font-normal">
                Дата
            </th>

            <th className="!relative !left-0 !w-20 !max-w-20 pr-2 !text-start !align-middle">
                Тип
            </th>

            <th className="!w-20 !max-w-20 pr-2 !text-start !align-middle">
                Договор №
            </th>

            {/* <th className="!w-20 pr-2 !text-start !align-middle font-normal">
                Дата начала
            </th> */}
            <th className="!w-40 pr-2 !text-start !align-middle font-normal">
                Статус
            </th>
            <th className="!w-20 pr-2 !text-start !align-middle">Акт №</th>
            <th className="!w-20 pr-2 !text-start !align-middle font-normal">
                Дата начала
            </th>
            <th className="!w-20 pr-2 !text-start !align-middle font-normal">
                Дата окончания
            </th>
            <th className="!w-20 pr-2 !text-start !align-middle font-normal">
                Сумма акта, <GreyRubleIconInline />
            </th>
            <th className="!w-20 pr-2 !text-start !align-middle font-normal">
                Бюджет, <GreyRubleIconInline />
            </th>
            <th className="!w-20 pr-2 !text-start !align-middle font-normal">
                Вознагр-ние <GreyRubleIconInline />
            </th>
            <th className="!w-40 pr-2 !text-start !align-middle font-normal">
                Статус
            </th>
        </tr>
    )

    const tableBody = (
        <>
            {formattedAgencyContracts.map((contract) => {
                const canDeleteContract =
                    contract.status === 'new' && !contract.actId

                const canEditContract = contract.acts.length === 0

                return (
                    <FrameworkContractRow
                        key={`contract-${contract.contractId}`}
                        contract={contract}
                        canDeleteContract={canDeleteContract}
                        canEditContract={canEditContract}
                        onEditContract={handleEditContract}
                        onDeleteContract={handleDeleteContract}
                        onAddAct={handleAddAct}
                        onEditAct={handleEditAct}
                        onDeleteAct={handleDeleteAct}
                        onUpdateContractStatus={
                            handleUpdateAgencyContractStatus
                        }
                        onUpdateActStatus={handleUpdateAgencyActStatus}
                    />
                )
            })}
        </>
    )

    return (
        <>
            <ConfirmDeleteDialog
                open={openDeleteDialog}
                onOpenChange={setOpenDeleteDialog}
                title={
                    deleteType === 'contract'
                        ? 'Удалить договор?'
                        : 'Удалить акт?'
                }
                message={
                    deleteType === 'contract'
                        ? 'У нас не будет возможности восстановить договор после его удаления.'
                        : 'У нас не будет возможности восстановить акт после его удаления.'
                }
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
            />
            <CreateAgencyContractDialog
                {...activeAwd}
                open={openContractDialog}
                onOpenChange={setOpenContractDialog}
                editingContract={editingContract}
            />

            <CreateAgencyActDialog
                {...activeAwd}
                open={openActDialog}
                onOpenChange={setOpenActDialog}
                editingAct={editingAct}
                contract={currentAgencyContract}
            />
            <div className="flex w-full max-w-[918px] flex-col gap-8 p-6">
                <div className="flex flex-col gap-2">
                    <h3 className="text-2xl font-extrabold leading-none">
                        Документы
                    </h3>
                    <p className="text text-sm leading-none text-zinc-950/40">
                        Рекламодатель:{' '}
                        <span className="font-bold">{advertiser?.name}</span>
                    </p>
                    <p className="text text-sm leading-none text-zinc-950/40">
                        Агентство:{' '}
                        <span className="font-bold">{agency?.name}</span>
                    </p>
                </div>
                <Button
                    className="m-0 max-w-52 gap-2 text-xs"
                    onClick={handleAddContract}
                >
                    Добавить новый договор
                    <PlusIcon className="h-4 w-4" />
                </Button>
            </div>

            <div className="pl-6">
                <Table
                    isLoading={false}
                    thead={tableHead}
                    tbody={tableBody}
                    baseTableClassName="pr-0 rounded-r-none"
                />
            </div>
        </>
    )
}
